import { logger } from '@adornis/base/logging.js';
import { existsSync } from 'node:fs';
import { Worker } from 'node:worker_threads';

/**
 * Diagnostic only — nothing gates on these numbers. Worth reading as three different scopes, because they are:
 * `rss` is the whole PROCESS including every worker isolate, while `heap` and `ext` are the CALLING isolate alone.
 *
 * That difference is why a memory BUDGET was never going to work here, and it is worth keeping now that the budget
 * is gone (see the header of `jobs/load.ts`): a worker gets its own V8 heap, and `getHeapStatistics()` /
 * `memoryUsage().heapUsed` report the CALLING isolate — so whatever sharp and pdf-lib allocate in here was
 * invisible to the check that decided whether another job could start, and most of it was external memory that
 * `--max-old-space-size` never governed either. What bounds this work now is the caller's own concurrency:
 * `sharp.concurrency(1)` below plus `PDF_EXTRACT_CONCURRENCY` and the OCR limiter at their call sites.
 *
 * These log lines are the only view of the numbers, which is why they are on every phase boundary rather than just
 * on failure.
 */
function fmtMem(): string {
  const m = process.memoryUsage();
  return `rss=${(m.rss / 1024 / 1024).toFixed(0)}MB heap=${(m.heapUsed / 1024 / 1024).toFixed(0)}MB ext=${(m.external / 1024 / 1024).toFixed(0)}MB`;
}

export interface EncodeEntry {
  fullPath: string;
  isImageFile: boolean;
  mimetype: string;
  ocrEnhance: boolean;
  resize: boolean;
  maxPx?: number;
  jpegQuality?: number;
}

interface EncodeJob {
  kind: 'encode';
  entries: EncodeEntry[];
}
interface SplitJob {
  kind: 'split';
  pdfBytes: Uint8Array;
  blocks: Array<{ pages: number[] }>;
}
type Job = EncodeJob | SplitJob;

type WorkerResult<T> = { ok: true; result: T } | { ok: false; error: { message: string; stack?: string } };

let activeWorkers = 0;

// Resolve the worker URL lazily — `transform-file.ts` (and therefore this module) is reached
// by agent registrations that the bundler also pulls into the client. `existsSync` is shimmed
// to undefined on the client, so calling it at module load would crash the page. We only ever
// invoke `runJob` server-side, so doing the check there keeps the client bundle inert.
let cachedWorkerURL: URL | null = null;
function resolveWorkerURL(): URL {
  if (cachedWorkerURL) return cachedWorkerURL;
  const distURL = new URL('./split-worker.js', import.meta.url);
  const srcURL = new URL('../../src/db/utils/split-worker.js', import.meta.url);
  cachedWorkerURL = existsSync(distURL) ? distURL : srcURL;
  return cachedWorkerURL;
}

function runJob<T>(job: Job): Promise<T> {
  const jobDesc =
    job.kind === 'encode' ? `encode ${job.entries.length} image(s)` : `split ${job.blocks.length} block(s)`;
  const t0 = Date.now();
  activeWorkers++;
  logger.info({ kind: job.kind, jobDesc, activeWorkers, mem: fmtMem(), context: 'worker:start' }, 'Worker started');

  return new Promise((resolve, reject) => {
    const worker = new Worker(resolveWorkerURL());
    let settled = false;

    const finish = (cb: () => void) => {
      if (settled) return;
      settled = true;
      activeWorkers--;
      void worker.terminate().finally(cb);
    };

    worker.once('message', (msg: WorkerResult<T>) => {
      if (msg?.ok) {
        finish(() => {
          logger.info(
            {
              kind: job.kind,
              jobDesc,
              elapsedMs: Date.now() - t0,
              activeWorkers,
              mem: fmtMem(),
              context: 'worker:done',
            },
            'Worker finished successfully',
          );
          resolve(msg.result);
        });
      } else {
        const errInfo = !msg?.ok ? msg.error : { message: 'Unknown worker error' };
        const err = new Error(errInfo.message);
        if (errInfo.stack) err.stack = errInfo.stack;
        finish(() => {
          logger.error(
            { kind: job.kind, elapsedMs: Date.now() - t0, activeWorkers, mem: fmtMem(), context: 'worker:error' },
            'Worker reported an error',
          );
          reject(err);
        });
      }
    });

    worker.once('error', err => {
      finish(() => {
        logger.error(
          { err, kind: job.kind, elapsedMs: Date.now() - t0, activeWorkers, mem: fmtMem(), context: 'worker:error' },
          'Worker threw an error',
        );
        reject(err instanceof Error ? err : new Error(String(err)));
      });
    });
    worker.once('exit', code => {
      if (settled) return;
      settled = true;
      activeWorkers--;
      logger.error(
        {
          kind: job.kind,
          code,
          elapsedMs: Date.now() - t0,
          activeWorkers,
          mem: fmtMem(),
          context: 'worker:exit-early',
        },
        'Worker exited early before posting a result',
      );
      reject(new Error(`split-worker exited with code ${code} before posting a result`));
    });
    const transferable = job.kind === 'split' ? [job.pdfBytes.buffer as ArrayBuffer] : [];
    worker.postMessage(job, transferable);
  });
}

export function encodeImagesInWorker(entries: EncodeEntry[]): Promise<string[]> {
  if (entries.length === 0) return Promise.resolve([]);
  return runJob<string[]>({ kind: 'encode', entries });
}

export function splitPdfInWorker(pdfBytes: Uint8Array, blocks: Array<{ pages: number[] }>): Promise<string[]> {
  if (blocks.length === 0) return Promise.resolve([]);
  return runJob<string[]>({ kind: 'split', pdfBytes, blocks });
}
